import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import { FeatherArrowLeft } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { validateExternalServiceAnswer } from "@/components/compliance/external-service-fields";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useOfflineComplianceCompletion } from "@/hooks/use-offline-compliance-completion";
import { useOfflineComplianceTaskInstance } from "@/hooks/use-offline-compliance-task-instance";
import { invalidateComplianceQueries } from "@/lib/compliance/invalidate-compliance-queries";
import { parseApiDateToLocalMidnight } from "@/lib/date-utils";

import { AwaitingCertificateView } from "./awaiting-certificate-view";
import { ChecklistCompletionForm } from "./checklist-completion-form";
import { CompletionConflict } from "./completion-conflict";
import { CompletionSummary } from "./completion-summary";
import { EvidenceUploadField } from "./evidence-upload-field";
import { ExternalServiceCompletionForm } from "./external-service-completion-form";
import { SimpleCompletionForm } from "./simple-completion-form";
import { SubmitBottomSheet } from "./submit-bottom-sheet";
import { SyncStatusBanner } from "./sync-status-banner";

type CompletionMode = "SIMPLE" | "CHECKLIST" | "EXTERNAL_SERVICE";

interface Props {
  instanceId: string;
}

export function ComplianceTaskInstanceCompletion({ instanceId }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    instance,
    definition,
    isLoading,
    isOffline,
    isServedFromCache,
    error,
    refetch,
  } = useOfflineComplianceTaskInstance(instanceId);

  // Hook is conditional on the data being present, so we initialise it with
  // safe placeholders and only render the body when both are available.
  const completion = useOfflineComplianceCompletion(
    instanceId,
    instance?.nurseryId ?? "",
  );

  const [showSubmitSheet, setShowSubmitSheet] = useState(false);
  // The user-supplied date (e.g. certificate expiry) is the next instance's
  // due date — required on the completing paths, optional when parking the
  // task as AWAITING_EVIDENCE.
  const [nextScheduledDate, setNextScheduledDate] = useState<Date | undefined>(
    undefined,
  );
  // The user-facing submission paths:
  //  - "complete": full COMPLETED — used by tasks without an evidence
  //    requirement, or by required-evidence tasks once the cert is uploaded.
  //  - "awaiting": park into AWAITING_EVIDENCE so the cert can be attached
  //    later. Surfaced as an explicit second button for evidence-optional
  //    tasks, and as the only path for required-evidence tasks before a
  //    file is uploaded.
  // Declared here (above the early returns) so the hook order stays stable
  // across renders that bail out into CompletionSummary / AwaitingCertificateView.
  const [pendingSubmissionMode, setPendingSubmissionMode] = useState<
    "complete" | "awaiting" | null
  >(null);

  // Pre-fill the next due date to the window's target (the latest allowed date,
  // i.e. previous due + frequency) (NAU-302). Re-seeds when the window changes,
  // because the cached instance renders before the fresh fetch and the window
  // can move in between (an overdue task's slides daily). A date the user
  // picked (or cleared) is kept while it still fits.
  const userPickedNextDue = useRef(false);
  const windowEarliestIso = instance?.nextDueWindow?.earliest;
  const windowTargetIso = instance?.nextDueWindow?.target;
  useEffect(() => {
    if (!windowTargetIso) return;
    const target = parseApiDateToLocalMidnight(windowTargetIso);
    const earliest = windowEarliestIso
      ? parseApiDateToLocalMidnight(windowEarliestIso)
      : undefined;
    setNextScheduledDate((current) => {
      if (!userPickedNextDue.current) return target;
      if (!current) return current;
      const fitsWindow =
        (!earliest || current >= earliest) && current <= target;
      return fitsWindow ? current : target;
    });
  }, [windowEarliestIso, windowTargetIso]);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate({ to: "/checklists" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Loader size="medium" />
      </div>
    );
  }

  if (error || !instance || !definition) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-4 py-12">
        <span className="text-body font-body text-subtext-color">
          {isOffline
            ? "This task isn't available offline yet. Connect to the internet to load it."
            : "We couldn't load this compliance task."}
        </span>
        {!isOffline && <Button onClick={() => refetch()}>Try again</Button>}
      </div>
    );
  }

  // Server says it's already completed by someone else (or by us in a previous
  // session) — render the summary view, not the form.
  if (instance.status === "COMPLETED") {
    return (
      <CompletionSummary
        instance={instance}
        definition={definition}
        onBack={handleBack}
        isServedFromCache={isServedFromCache}
      />
    );
  }

  // Submitted earlier without a certificate — render the dedicated attach view
  // so the form body can't be re-submitted, but the certificate can be added.
  if (instance.status === "AWAITING_EVIDENCE") {
    return (
      <AwaitingCertificateView
        instance={instance}
        definition={definition}
        onBack={handleBack}
        onAttached={async () => {
          // Owns the whole sweep for the attach path — the child view
          // deliberately doesn't invalidate, so this runs once. The detail
          // query behind `refetch` is one of the keys the helper covers and
          // awaits, so a separate refetch here would only duplicate it.
          await invalidateComplianceQueries(queryClient);
        }}
      />
    );
  }

  // Rejected while the task is still open — a completion by someone else falls
  // into the CompletionSummary branch above instead.
  if (completion.hasConflict) {
    return (
      <CompletionConflict
        notes={completion.notes}
        reason={completion.rejectionReason}
        onBack={handleBack}
        onResubmit={async () => {
          try {
            // Fetch first so the form reappears with the current window (or
            // the summary, if someone else did finish it), not the cached one.
            await refetch();
            await completion.clearRejection();
          } catch (err) {
            toast.error("Couldn't reopen the form — try again");
            console.error("compliance clear rejection failed", err);
          }
        }}
      />
    );
  }

  const mode = definition.completionMode as CompletionMode;
  const evidenceRequired = definition.evidenceRequired;

  // The validated next-due window (NAU-302). Present when the definition has a
  // frequency; absent for legacy tasks, which fall back to a floor-only picker.
  // Parse the bounds to LOCAL midnight so they compare correctly against the
  // calendar's local-midnight day cells (the API serializes them as UTC midnight).
  const nextDueWindow = instance.nextDueWindow ?? null;
  const windowEarliest = nextDueWindow
    ? parseApiDateToLocalMidnight(nextDueWindow.earliest)
    : undefined;
  const windowTarget = nextDueWindow
    ? parseApiDateToLocalMidnight(nextDueWindow.target)
    : undefined;

  // ---- Submit gating ----
  // The bottom sheet is the deliberate audit moment. It's only reachable once
  // the form is locally complete enough that submit could succeed — the BE
  // will catch shape errors but we'd like to fail-fast in the UI too.
  const isFormReady = (() => {
    if (mode === "CHECKLIST") {
      const allItemsMarked = definition.checklistItemDefinitions.every(
        (item) => completion.checklistResults[item.id]?.result != null,
      );
      if (!allItemsMarked) return false;
    }
    if (mode === "EXTERNAL_SERVICE") {
      const config = definition.externalServiceConfig as
        | { fields?: Array<{ name: string; type?: string; required: boolean }> }
        | undefined;
      const fields = config?.fields ?? [];
      // Every field must satisfy its type + required rule — same logic the BE
      // enforces, so the UI gates submission rather than letting it 400.
      const allValid = fields.every(
        (f) =>
          validateExternalServiceAnswer(
            f.type ?? "text",
            completion.externalServiceData[f.name],
            f.required,
          ) === null,
      );
      if (!allValid) return false;
    }
    // Evidence is NO LONGER a hard gate on submit. When evidence is required
    // but missing, submission routes the instance into AWAITING_EVIDENCE so
    // the certificate can be attached later — the practitioner doesn't need
    // to wait for the cert before recording that the work was done.
    return true;
  })();

  // `pendingSubmissionMode` (declared above the early returns) is set when
  // the user clicks one of the footer buttons; it drives both the bottom-
  // sheet disclosure and the BE routing.
  const willAwaitEvidence = pendingSubmissionMode === "awaiting";

  const hasNextDueDate = nextScheduledDate != null;
  // Which mode the footer offers is a pure function of the definition + upload
  // state, so it is known before any button is clicked — unlike
  // `pendingSubmissionMode`, which is only set by the click itself.
  const completePathOffered =
    !evidenceRequired || completion.hasUploadedEvidence;
  const canCompleteNow = isFormReady && hasNextDueDate;

  const handleConfirmSubmit = async () => {
    try {
      const { rejected } = await completion.submitCompletion({
        nextDueDate: nextScheduledDate
          ? format(nextScheduledDate, "yyyy-MM-dd")
          : undefined,
        submitAsAwaiting:
          pendingSubmissionMode === "awaiting" ? true : undefined,
      });
      setShowSubmitSheet(false);
      setPendingSubmissionMode(null);
      // Stay on the screen so the user sees why. A rejection usually means the
      // cache is behind the server (e.g. someone else finished the task), so
      // sweep it — that also refetches this instance.
      if (rejected) {
        await invalidateComplianceQueries(queryClient);
        return;
      }
      toast.success(
        willAwaitEvidence
          ? "Task submitted. Add your certificate when you're ready."
          : completion.syncStatus.isOnline
            ? "Task submitted — syncing"
            : "Saved locally — will sync when you're back online",
      );
      // Sweep every surface this completion touched, not just the To-do list:
      // the server also schedules the next instance, opens follow-up actions
      // for failed checklist items, writes evidence rows, and moves the
      // dashboard status behind the summary cards and priority list. The
      // Compliance Manager's tabs in particular used to keep serving their
      // pre-completion cache until the user hard-refreshed.
      //
      // The helper invalidates with `refetchType: "all"`, which is mandatory
      // because the global QueryClient sets `refetchOnMount: false` — a merely
      // stale-marked query would silently render the old rows on remount. It
      // also resolves only once those refetches settle, including for queries
      // with no observers, so awaiting it leaves the destination page's cache
      // already warm and no second explicit refetch is needed.
      await invalidateComplianceQueries(queryClient);
      navigate({ to: "/checklists" });
    } catch (err) {
      toast.error("Couldn't submit — try again");
      console.error("compliance submit failed", err);
    }
  };

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-1 pb-6">
        <div className="flex w-full items-center gap-2">
          <IconButton
            variant="neutral-secondary"
            icon={<FeatherArrowLeft />}
            aria-label="Back"
            onClick={handleBack}
          />
          <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
            {definition.name}
          </span>
        </div>
        {definition.description && (
          <span className="pl-12 text-body font-body text-subtext-color">
            {definition.description}
          </span>
        )}
      </div>

      <SyncStatusBanner
        syncStatus={completion.syncStatus}
        onRetry={completion.retryFailed}
      />

      {/* Mode-specific body */}
      <div className="flex w-full max-w-2xl mx-auto flex-col items-start gap-6 pb-32">
        {mode === "SIMPLE" && (
          <SimpleCompletionForm
            notes={completion.notes}
            onNotesChange={completion.setNotes}
          />
        )}

        {mode === "CHECKLIST" && (
          <ChecklistCompletionForm
            items={definition.checklistItemDefinitions}
            results={completion.checklistResults}
            onResultChange={completion.setChecklistItemResult}
            notes={completion.notes}
            onNotesChange={completion.setNotes}
          />
        )}

        {mode === "EXTERNAL_SERVICE" && (
          <ExternalServiceCompletionForm
            config={definition.externalServiceConfig}
            data={completion.externalServiceData}
            onFieldChange={completion.setExternalServiceField}
            notes={completion.notes}
            onNotesChange={completion.setNotes}
          />
        )}

        <EvidenceUploadField
          photos={completion.photos}
          onAddPhoto={completion.savePhoto}
          onDeletePhoto={completion.deletePhoto}
          required={evidenceRequired}
        />

        {/* Next scheduled instance — sets dueDate for the auto-created
            successor instance. Captured on every submission path (including
            AWAITING_EVIDENCE) so the practitioner can record their best
            guess at the cert expiry up front. They can still edit it at
            attach-evidence time if the actual cert reveals a different date. */}
        <div className="flex w-full flex-col items-start gap-2">
          <label className="text-body-bold font-body-bold text-neutral-900">
            Next due date
            {completePathOffered && (
              <span className="text-error-700 ml-1">*</span>
            )}
          </label>
          <span className="text-caption font-caption text-subtext-color">
            {windowEarliest && windowTarget
              ? `Typically the certificate expiry date. Pick a date between ${format(windowEarliest, "d MMM yyyy")} and ${format(windowTarget, "d MMM yyyy")} (set by this task's repeat frequency).`
              : "Typically the certificate expiry date. We'll schedule the next instance for this date."}
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex h-10 w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background px-3 text-body font-body text-neutral-900 hover:border-neutral-400"
              >
                <span>
                  {nextScheduledDate
                    ? format(nextScheduledDate, "EEEE, MMMM d, yyyy")
                    : "Select date"}
                </span>
                <svg
                  className="h-4 w-4 text-neutral-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={nextScheduledDate}
                // Open on the selected/target month, not today — the window can
                // be a year out, so defaulting to the current month would show an
                // all-disabled view.
                defaultMonth={nextScheduledDate ?? windowTarget}
                onSelect={(date) => {
                  userPickedNextDue.current = true;
                  setNextScheduledDate(date);
                }}
                disabled={(date) => {
                  const today = new Date(new Date().setHours(0, 0, 0, 0));
                  if (date < today) return true;
                  // Constrain to the validated window when the task has a
                  // frequency; the server enforces the same bounds (NAU-302).
                  if (windowEarliest && date < windowEarliest) return true;
                  if (windowTarget && date > windowTarget) return true;
                  return false;
                }}
                autoFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Sticky footer submit.
          Two button layouts depending on the definition + file state:
          - evidenceRequired = false: two buttons — practitioners on
            evidence-optional tasks can choose between "Save for later"
            (parks as AWAITING_EVIDENCE so a cert can be added later) and
            "Complete now" (full COMPLETED).
          - evidenceRequired = true + no file: one button — "Submit (awaiting
            cert)" is the only path forward; the cert is mandatory but can
            arrive later.
          - evidenceRequired = true + file uploaded: one button — "Submit &
            complete" finishes the task. */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-neutral-border bg-white px-4 py-3 shadow-md">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 mobile:flex-col mobile:items-stretch">
          <span className="text-caption font-caption text-subtext-color mobile:text-center">
            {!isFormReady
              ? mode === "CHECKLIST"
                ? `${
                    definition.checklistItemDefinitions.filter(
                      (i) => completion.checklistResults[i.id]?.result != null,
                    ).length
                  } of ${definition.checklistItemDefinitions.length} marked`
                : "Some required fields are missing"
              : completePathOffered && !hasNextDueDate
                ? "Add the next due date to complete"
                : "Ready to submit"}
          </span>
          {evidenceRequired ? (
            <Button
              disabled={completePathOffered ? !canCompleteNow : !isFormReady}
              onClick={() => {
                setPendingSubmissionMode(
                  completePathOffered ? "complete" : "awaiting",
                );
                setShowSubmitSheet(true);
              }}
            >
              {completePathOffered
                ? "Submit & complete"
                : "Submit (awaiting cert)"}
            </Button>
          ) : (
            <div className="flex items-center gap-2 mobile:w-full">
              <Button
                variant="neutral-secondary"
                disabled={!isFormReady}
                onClick={() => {
                  setPendingSubmissionMode("awaiting");
                  setShowSubmitSheet(true);
                }}
              >
                Evidence pending
              </Button>
              <Button
                disabled={!canCompleteNow}
                onClick={() => {
                  setPendingSubmissionMode("complete");
                  setShowSubmitSheet(true);
                }}
              >
                Complete now
              </Button>
            </div>
          )}
        </div>
      </div>

      {showSubmitSheet && (
        <SubmitBottomSheet
          definitionName={definition.name}
          mode={mode}
          failureCount={completion.failureCount}
          willAwaitEvidence={willAwaitEvidence}
          onCancel={() => {
            setShowSubmitSheet(false);
            setPendingSubmissionMode(null);
          }}
          onConfirm={handleConfirmSubmit}
        />
      )}
    </div>
  );
}
